<div class="section-header">
    <mat-icon class="section-icon">auto_awesome</mat-icon>
    <span class="section-title">{{ 'ai.jsonPlayerWizard' | transloco }}</span>
    <button mat-icon-button class="close-button" (click)="onClose()">
        <mat-icon>close</mat-icon>
    </button>
</div>

<div class="stepper-wrapper">
    <div class="stepper-btn">
        @if (selectedStep != 0) {
            <button mat-raised-button color="warn" (click)="stepper.previous()">
                <mat-icon class="hide-lt-sm">skip_previous</mat-icon>
                {{ "general.back" | transloco }}
            </button>
        }
        @if (selectedStep != 2) {
            <button mat-raised-button color="warn" (click)="stepper.next()">
                <mat-icon class="hide-lt-sm">skip_next</mat-icon>
                {{ "general.next" | transloco }}
            </button>
        }
    </div>

    <mat-horizontal-stepper linear #stepper (selectionChange)="onStepChange($event)">
        <mat-step>
            <ng-template matStepLabel>{{ "ai.createFeed" | transloco }}</ng-template>
            <div class="step-body-wrapper">
                <app-dialog-any-feed
                    [wizardMode]="true"
                    (feedCreated)="onFeedCreated($event)"
                    (cancelled)="onFeedCancelled()">
                </app-dialog-any-feed>
            </div>
        </mat-step>

        <mat-step>
            <ng-template matStepLabel>{{ "ai.fieldMapping" | transloco }}</ng-template>
            <div class="step-body-wrapper">
                <div class="field-mapping-section">
                    <div class="field-mapping-header">
                        <mat-icon class="section-icon">view_list</mat-icon>
                        <span class="section-title">{{ 'ai.defineFields' | transloco }}</span>
                    </div>

                    <div class="field-mapping-hint">
                        {{ 'ai.fieldMappingHint' | transloco }}
                    </div>

                    <!-- Field List -->
                    <div class="fields-container">
                        @for (field of fields; track $index; let i = $index) {
                            <div class="field-row">
                                <div class="field-number">{{ i + 1 }}</div>
                                <div class="field-inputs">
                                    <mat-form-field appearance="outline" class="field-name-input">
                                        <mat-label>{{ 'ai.fieldName' | transloco }}</mat-label>
                                        <input
                                            matInput
                                            [(ngModel)]="field.name"
                                            (input)="onFieldNameChange(i, $event)"
                                            [placeholder]="'ai.fieldNamePlaceholder' | transloco"
                                            maxlength="30"
                                        />
                                        @if (field.nameError) {
                                            <mat-error>{{ field.nameError }}</mat-error>
                                        }
                                    </mat-form-field>

                                    <mat-icon class="arrow-icon">arrow_forward</mat-icon>

                                    <mat-form-field appearance="outline" class="field-description-input">
                                        <mat-label>{{ 'ai.fieldDescription' | transloco }}</mat-label>
                                        <input
                                            matInput
                                            [(ngModel)]="field.description"
                                            [placeholder]="'ai.fieldDescriptionPlaceholder' | transloco"
                                            maxlength="200"
                                        />
                                    </mat-form-field>
                                </div>
                                <div class="field-actions">
                                    <button
                                        mat-icon-button
                                        class="add-field-btn"
                                        (click)="addField()"
                                        [matTooltip]="'general.add' | transloco"
                                    >
                                        <mat-icon>add_circle</mat-icon>
                                    </button>
                                    <button
                                        mat-icon-button
                                        class="remove-field-btn"
                                        (click)="removeField(i)"
                                        [disabled]="fields.length === 1"
                                        [matTooltip]="'general.remove' | transloco"
                                    >
                                        <mat-icon>remove_circle</mat-icon>
                                    </button>
                                </div>
                            </div>
                        }
                    </div>
                </div>
            </div>
        </mat-step>

        <mat-step>
            <ng-template matStepLabel>{{ "ai.preview" | transloco }}</ng-template>
            <div class="step-body-wrapper">
                <div class="preview-section">
                    <div class="preview-header">
                        <mat-icon class="section-icon">preview</mat-icon>
                        <span class="section-title">{{ 'ai.dataPreview' | transloco }}</span>
                    </div>

                    @if (isLoadingPreview) {
                        <div class="preview-loading">
                            <mat-spinner diameter="60"></mat-spinner>
                            <div class="loading-text">{{ 'ai.loadingPreview' | transloco }}...</div>
                        </div>
                    }

                    @if (previewError) {
                        <div class="preview-error">
                            <mat-icon>error</mat-icon>
                            <span>{{ previewError }}</span>
                            <button mat-raised-button color="warn" (click)="loadPreview()">
                                <mat-icon>refresh</mat-icon>
                                {{ 'general.retry' | transloco }}
                            </button>
                        </div>
                    }

                    @if (!isLoadingPreview && !previewError && previewData && previewData.length > 0) {
                        <div class="preview-success">
                            <div class="preview-stats">
                                <mat-icon>check_circle</mat-icon>
                                <span>{{ 'ai.foundResults' | transloco:{ count: previewData.length } }}</span>
                            </div>

                            <div class="preview-table-container">
                                <table class="preview-table">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            @for (field of fields; track field.name) {
                                                <th>{{ field.name }}</th>
                                            }
                                        </tr>
                                    </thead>
                                    <tbody>
                                        @for (item of previewData; track $index; let i = $index) {
                                            <tr>
                                                <td class="row-number">{{ i + 1 }}</td>
                                                @for (field of fields; track field.name) {
                                                    <td>{{ item[field.name] || '-' }}</td>
                                                }
                                            </tr>
                                        }
                                    </tbody>
                                </table>
                            </div>

                            <form [formGroup]="propertyFormGroup" class="final-form">
                                <mat-form-field appearance="outline" class="player-name-field">
                                    <mat-label>{{ "ai.playerName" | transloco }}</mat-label>
                                    <input autocomplete="off" type="text" matInput formControlName="playerName" required />
                                </mat-form-field>

                                <div class="action-buttons">
                                    <button mat-raised-button color="primary" (click)="onClose()">
                                        {{ "general.cancel" | transloco }}
                                    </button>
                                    <button mat-raised-button color="warn" (click)="onCreate()" [disabled]="!propertyFormGroup.valid">
                                        <mat-icon>check_circle</mat-icon>
                                        {{ "ai.create" | transloco }}
                                    </button>
                                </div>
                            </form>
                        </div>
                    }
                </div>
            </div>
        </mat-step>
    </mat-horizontal-stepper>
</div>
